import axios, { AxiosInstance, AxiosError, AxiosRequestHeaders } from 'axios';
import uuid from 'uuid/v4';
import isNil from 'lodash/isNil';
import VueRouter, { Route } from 'vue-router';
import { Store } from 'vuex';

import { Config } from '@/environment';
import { AppState } from '@/store';
import { AppUiService } from './ui/ui.service';
import { Logger } from './logger';
import { CustomerType } from '@/models';
import { Routes } from '@/router';

import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { MUTATIONS as UI_MUTATIONS } from '@/store/ui';
import { HEADER_KEY_CORRELATION_ID } from './api/api.service';
import { makeAuthorizationHeader, getCountryByHostname } from '@/helpers';

export function makeCmsHttpClient(config: Config): AxiosInstance {
  const countryCode = getCountryByHostname(window.location.hostname).countryCode;
  return axios.create({
    baseURL: countryCode === 'CA' ? config.cms.baseUrlCanada : config.cms.baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function makeApiGatewayHttpClient(config: Config, store: Store<AppState>, uiService: AppUiService, router: VueRouter, logger: Logger): AxiosInstance {
  const client = axios.create({
    baseURL: config.services.gatewayUrl,
    headers: {
      'Ocp-Apim-Subscription-Key': config.services.gatewaySubscriptionKey,
      'Content-Type': 'application/json',
    },
  });

  // Automatically add authorization and correlation id headers
  client.interceptors.request.use(req => {
    if (req.baseURL) {
      logger.debug(`Start API request: ${req.baseURL.substring(0, req.baseURL.length - 1)}${req.url}. Correlation ID: ${req.headers[HEADER_KEY_CORRELATION_ID]}`);
    }

    makeCorrelationIdHeader(req.headers);
    makeAuthorizationHeader(req.headers, store.state.customer.jwt);

    return req;
  });

  // Automatically drop jwt on 401
  client.interceptors.response.use(
    res => {
      logger.debug(`End API request: ${res.config.url}`);
      return res;
    },
    (err: AxiosError | any) => {
      const status = err && err.response && err.response.status;
      if (status === 401) {
        const isGuest = !isNil(store.state.customer.type) && [CustomerType.Guest, CustomerType.AutoGenerated].includes(store.state.customer.type!);
        // TODO-d8ehf5: maybe find another way to do this? I don't like bypassing
        // the services like this, but I can't think of another way that doesn't
        // introduce the possibility of infinite loops (aside from directly
        // exposing a method to set the jwt and pass in a getter fn to the enclosing method?)
        store.commit(CUSTOMER_MUTATIONS.SET_JWT, undefined);
        store.commit(CUSTOMER_MUTATIONS.RESET_PROFILE);

        // Close any existing modal or bag so the user lands on the undisturbed sign in page
        uiService.closeAllOverlays();
        const showSessionExpiredBanner = ![
          Routes.VendorSignIn,
          Routes.VendorCreateAccount,
          Routes.VendorPasswordResetRequestPage,
          Routes.Home,
          Routes.PasswordResetPage,
          Routes.PasswordResetRequestPage,
          Routes.SignIn,
          Routes.CreateAccount,
          Routes.NutritionCalculatorPage,
          Routes.NutritionCalculcatorMealBuilder,
        ].includes(router.currentRoute.name as Routes);

        store.commit(UI_MUTATIONS.SET_SESSION_EXPIRED_BANNER, showSessionExpiredBanner);

        return Promise.reject(err);
      }
      return Promise.reject(err);
    },
  );

  return client;
}

function makeCorrelationIdHeader(headers: AxiosRequestHeaders) {
  headers[HEADER_KEY_CORRELATION_ID] =  `OrderWeb-${uuid()}`;
}
